import { AxiosResponse } from "axios";
import { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useState } from "react";

import { useAxiosClient } from "@/hooks";
import { useFileUpload } from "@/hooks/storage";
import {
  CreateAdhocGroupRequest,
  UpdateAdhocGroupRequest,
} from "@/types/requests";
import {
  CreateAdhocGroupResponse,
  GetAdhocGroupsResponse,
  UpdateAdhocGroupResponse,
} from "@/types/responses";

const useGetGroups = (questionId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const client = useAxiosClient("/adhoc/questions");

  const fetchGroups = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      const response = await client.get<GetAdhocGroupsResponse>(
        `/${questionId}/groups`
      );
      return response.data.groups;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, [client, questionId]);

  return {
    error,
    fetchGroups,
    loading,
  };
};

const useCreateGroup = (questionId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const client = useAxiosClient("/adhoc/questions");
  const { uploadFile } = useFileUpload();

  const createGroup = useCallback(
    async (
      data: Omit<CreateAdhocGroupRequest, "media"> & {
        media: ImagePickerAsset[];
      }
    ) => {
      setError(null);
      setLoading(true);
      setUploadProgress(0);

      try {
        const fileCount = data.media.length;
        const fileProgresses: number[] = new Array(fileCount).fill(0);

        const mediaPaths: string[] = await Promise.all(
          data.media.map(async (item, index) => {
            return uploadFile({
              file: item,
              folder: "adhoc/groups",
              resourceId: questionId,
              onProgress: (progress) => {
                fileProgresses[index] = progress;
                const totalProgress =
                  fileCount > 0
                    ? fileProgresses.reduce((sum, p) => sum + p, 0) / fileCount
                    : 0;
                setUploadProgress(totalProgress);
              },
            });
          })
        );

        const response = await client.post<
          CreateAdhocGroupResponse,
          AxiosResponse<CreateAdhocGroupResponse>,
          CreateAdhocGroupRequest
        >(`/${questionId}/groups`, {
          description: data.description?.trim() || null,
          demographics: data.demographics,
          media: mediaPaths,
          name: data.name?.trim() || null,
          panel_id: data.panel_id,
          size: data.size,
        });
        return response.data.group;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    },
    [client, questionId, uploadFile]
  );

  return {
    createGroup,
    error,
    loading,
    uploadProgress,
  };
};

const useUpdateGroup = (questionId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const client = useAxiosClient("/adhoc/questions");
  const { uploadFile } = useFileUpload();

  const updateGroup = useCallback(
    async (
      groupId: string,
      data: Omit<UpdateAdhocGroupRequest, "media"> & {
        media: (ImagePickerAsset | string)[];
      }
    ) => {
      setError(null);
      setLoading(true);
      setUploadProgress(0);

      try {
        const newFiles = data.media.filter((item) => typeof item !== "string");
        const fileCount = newFiles.length;
        const fileProgresses: number[] = new Array(fileCount).fill(0);
        let newFileIndex = 0;

        const mediaPaths: string[] = await Promise.all(
          data.media.map(async (item) => {
            if (typeof item === "string") {
              return item;
            }
            const currentIndex = newFileIndex++;
            return uploadFile({
              file: item,
              folder: "adhoc/groups",
              resourceId: questionId,
              onProgress: (progress) => {
                fileProgresses[currentIndex] = progress;
                const totalProgress =
                  fileCount > 0
                    ? fileProgresses.reduce((sum, p) => sum + p, 0) / fileCount
                    : 0;
                setUploadProgress(totalProgress);
              },
            });
          })
        );

        const response = await client.put<
          UpdateAdhocGroupResponse,
          AxiosResponse<UpdateAdhocGroupResponse>,
          UpdateAdhocGroupRequest
        >(`/${questionId}/groups/${groupId}`, {
          description: data.description?.trim() || null,
          demographics: data.demographics,
          media: mediaPaths,
          name: data.name?.trim() || null,
          panel_id: data.panel_id,
          size: data.size,
        });
        return response.data.group;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    },
    [client, questionId, uploadFile]
  );

  return {
    error,
    loading,
    updateGroup,
    uploadProgress,
  };
};

const useDeleteGroup = (questionId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/adhoc/questions");

  const deleteGroup = useCallback(
    async (groupId: string): Promise<boolean> => {
      setError(null);
      setLoading(true);

      try {
        await client.delete(`/${questionId}/groups/${groupId}`);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client, questionId]
  );

  return {
    deleteGroup,
    error,
    loading,
  };
};

export { useCreateGroup, useDeleteGroup, useGetGroups, useUpdateGroup };
